'use client';

import { createContext, useContext } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import PengiModal from '@/components/Modal/pengiModal';
import NormalButton from '@/components/Button/normal';

type PublicStudyAuthPromptReason =
  | 'ask-pengi'
  | 'explain'
  | 'more-details'
  | 'live'
  | 'complete-question'
  | 'assign';

type PublicStudyAuthPromptContextValue = {
  requestAuthPrompt: (reason: PublicStudyAuthPromptReason) => boolean;
};

const PublicStudyAuthPromptContext = createContext<PublicStudyAuthPromptContextValue>({
  requestAuthPrompt: () => false,
});

export function PublicStudyAuthPromptProvider({
  children,
  requestAuthPrompt,
}: {
  children: React.ReactNode;
  requestAuthPrompt: (reason: PublicStudyAuthPromptReason) => boolean;
}) {
  return (
    <PublicStudyAuthPromptContext.Provider value={{ requestAuthPrompt }}>
      {children}
    </PublicStudyAuthPromptContext.Provider>
  );
}

export function usePublicStudyAuthPrompt() {
  return useContext(PublicStudyAuthPromptContext);
}

function getPromptCopy(reason: PublicStudyAuthPromptReason) {
  const unifiedCopy = {
    title: 'Sign in to continue',
    description: 'Save your progress and keep learning from where you left off.',
  };
  if (reason === 'assign') {
    return {
      title: 'Sign in to assign',
      description: 'Sign in to assign this lesson and track your child’s progress.',
    };
  }
  void reason;
  return unifiedCopy;
}

export function PublicStudyAuthPromptModal({
  visible,
  reason,
  hideContext = false,
  onClose,
}: {
  visible: boolean;
  reason: PublicStudyAuthPromptReason;
  hideContext?: boolean;
  onClose: () => void;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const copy = getPromptCopy(reason);

  const handleSignIn = () => {
    const current = searchParams.toString();
    const redirectTo = current ? `${pathname}?${current}` : pathname;
    onClose();
    router.push(`/?show_login=1&redirect=${encodeURIComponent(redirectTo)}`);
  };

  return (
    <PengiModal visible={visible} onClick={onClose} showFooterButton={false}>
      <div className="flex flex-col items-center gap-4 bg-white px-2 py-4 text-center">
        <div className="max-w-xl">
          <div className="text-3xl font-bold leading-tight text-[#1F2A44]">{copy.title}</div>
          <div className="mt-3 text-base leading-7 text-[#667085]">{copy.description}</div>
        </div>
        {!hideContext ? (
          <div className="text-sm text-[#8D93A1]">It only takes a moment.</div>
        ) : null}
        <div className="mt-1 flex w-full flex-col gap-3">
          <NormalButton onClick={handleSignIn}>
            Sign in
          </NormalButton>
        </div>
      </div>
    </PengiModal>
  );
}

export type { PublicStudyAuthPromptReason };
